/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    SlotPriorityUpdate,
    WarehousesService,
    ZoneLocationViewModel,
    ZonePriorityUpdate,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { WarehouseStore } from './warehouse.store';
import {
    WarehouseCreate,
    WarehouseRead,
    WarehouseSearchParams,
    WarehouseStructure,
} from '../../entities/warehouse.dto';
import { Operation } from 'fast-json-patch';
import { ZoneRead } from '../../entities/zone.dto';
import { ZoneStore } from '../zones/zone.store';
import { SlotStore } from '../slots/slot.store';
import { SlotRead } from '../../entities/slot.dto';
import { asArraySafe } from '@pf/shared-utility';

@Injectable()
export class WarehouseDataService extends AbstractDataService<
    WarehouseRead,
    WarehouseCreate
> {
    private readonly zoneStore = inject(ZoneStore);
    private readonly slotStore = inject(SlotStore);
    protected EntityName = PFWmsEntities.Warehouse;
    constructor(
        private service: WarehousesService,
        store: WarehouseStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: WarehouseCreate): Observable<WarehouseRead> {
        return this.service.warehouseCreate({
            warehouseViewModelCreateBody: body,
        }) as Observable<WarehouseRead>;
    }
    protected deleteObs$(id: string): Observable<WarehouseRead> {
        return this.service.warehouseDelete({
            id,
        }) as Observable<WarehouseRead>;
    }
    protected undoDeleteObs$(id: string): Observable<WarehouseRead> {
        return this.service.warehouseUndoDelete({
            id,
        }) as Observable<WarehouseRead>;
    }
    protected getObs$(id: string): Observable<WarehouseRead> {
        return this.service.warehouseReadById({
            id,
        }) as Observable<WarehouseRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<WarehouseRead> {
        return this.service.warehouseUpdatePatch({
            id,
            operation: operations,
        }) as Observable<WarehouseRead>;
    }
    protected searchObs$(
        searchParams: WarehouseSearchParams
    ): Observable<IPagedResult<WarehouseRead>> {
        return this.service.warehouseSearch(searchParams) as Observable<
            IPagedResult<WarehouseRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<WarehouseRead>>> {
        return this.service.warehouseGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<WarehouseRead>>
        >;
    }

    getStructure$(id: string): Observable<WarehouseStructure> {
        this._logger.debug(`Fetching structure for warehouse ${id}`);
        return this.service
            .v1WarehousesWarehouseIdStructureGet({
                warehouseId: id,
            })
            .pipe(
                tap(structure => {
                    structure.forEach(zoneLocation => {
                        this.addZoneToStore(zoneLocation);
                    });
                })
            );
    }

    updateStructure$(
        id: string,
        zones: ZonePriorityUpdate[],
        slots: SlotPriorityUpdate[]
    ): Observable<WarehouseStructure> {
        return this.service.v1WarehousesWarehouseIdStructurePost({
            warehouseId: id,
            warehouseStructureUpdateViewModel: {
                zones,
                slots,
            },
        });
    }

    private addZoneToStore(zoneLocation: ZoneLocationViewModel) {
        if (zoneLocation.zone) {
            const zone = zoneLocation.zone as ZoneRead;
            this.zoneStore.add(zone, false);

            if (zone.slots?.length) {
                zone.slots.forEach(slot => {
                    this.slotStore.add(slot as SlotRead, false);
                });
            }
        } else {
            asArraySafe(zoneLocation.children).forEach(child => {
                this.addZoneToStore(child);
            });
        }
    }
}
